
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Check, CheckCheck, Loader2, MessageCircle, Trash2, Reply, CalendarClock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const Messages = () => {
  const [newMessage, setNewMessage] = useState("");
  const [replyTo, setReplyTo] = useState<{ id: string; sender: string } | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<any>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        setCurrentUserId(data.user?.id || null);
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    
    fetchCurrentUser();
  }, []);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  };

  const { data: allMessages, isLoading, isError, refetch } = useQuery({
    queryKey: ["messages"],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { data, error } = await supabase
          .from("messages")
          .select(`
            *,
            sender:profiles!sender_id(*),
            receiver:profiles!receiver_id(*)
          `)
          .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
          .order("created_at", { ascending: false });

        if (error) throw error;
        return data;
      } catch (error) {
        console.error("Error fetching messages:", error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000,
  });

  const conversations = allMessages ? processConversations(allMessages, currentUserId) : [];
  
  function processConversations(messages, userId) {
    const conversationsMap = new Map();
    
    messages.forEach(message => {
      const otherPersonId = message.sender_id === userId ? message.receiver_id : message.sender_id;
      const otherPerson = message.sender_id === userId ? message.receiver : message.sender;
      
      if (!conversationsMap.has(otherPersonId)) {
        conversationsMap.set(otherPersonId, {
          id: otherPersonId,
          person: otherPerson,
          messages: [],
          lastMessageTime: new Date(message.created_at),
          unreadCount: message.receiver_id === userId && !message.read ? 1 : 0
        });
      } else {
        const conversation = conversationsMap.get(otherPersonId);
        if (message.receiver_id === userId && !message.read) {
          conversation.unreadCount += 1;
        }
        
        const messageTime = new Date(message.created_at);
        if (messageTime > conversation.lastMessageTime) {
          conversation.lastMessageTime = messageTime;
        }
      }
      
      const conversationToUpdate = conversationsMap.get(otherPersonId);
      conversationToUpdate.messages.push(message);
    });
    
    return Array.from(conversationsMap.values())
      .sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime());
  }

  const currentMessages = selectedConversation 
    ? selectedConversation.messages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    : [];

  useEffect(() => {
    if (conversations.length > 0 && !selectedConversation) {
      setSelectedConversation(conversations[0]);
    }
    
    if (currentMessages.length && !isLoading) {
      setTimeout(scrollToBottom, 100);
    }
  }, [currentMessages.length, isLoading, conversations, selectedConversation]);

  const sendMessage = useMutation({
    mutationFn: async ({ content, receiverId }: { content: string; receiverId: string }) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { error } = await supabase
          .from("messages")
          .insert({
            content,
            sender_id: user.id,
            receiver_id: receiverId,
          });

        if (error) throw error;
      } catch (error) {
        console.error("Error sending message:", error);
        throw error;
      }
    },
    onSuccess: () => {
      setNewMessage("");
      setReplyTo(null);
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast.success("Message envoyé");
      setTimeout(scrollToBottom, 100);
    },
    onError: () => {
      toast.error("Impossible d'envoyer le message.");
    },
  });

  const deleteMessage = useMutation({
    mutationFn: async (messageId: string) => {
      try {
        const { error } = await supabase
          .from("messages")
          .delete()
          .eq("id", messageId);

        if (error) throw error;
      } catch (error) {
        console.error("Error deleting message:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast.success("Message supprimé");
      setMessageToDelete(null);
      setDeleteDialogOpen(false);
    },
    onError: () => {
      toast.error("Impossible de supprimer le message.");
    },
  });

  const markAsRead = useMutation({
    mutationFn: async (messageId: string) => {
      try {
        const { error } = await supabase
          .from("messages")
          .update({ read: true })
          .eq("id", messageId);

        if (error) throw error;
      } catch (error) {
        console.error("Error marking message as read:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
    onError: (error) => {
      console.error("Mark as read error:", error);
    }
  });

  const handleSendMessage = () => {
    if (!selectedConversation || !newMessage.trim()) return;

    sendMessage.mutate({
      content: newMessage,
      receiverId: selectedConversation.id,
    });
  };

  const handleDeleteRequest = (messageId: string) => {
    setMessageToDelete(messageId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (messageToDelete) {
      deleteMessage.mutate(messageToDelete);
    }
  };

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8 flex flex-col items-center">
        <p className="text-red-500 mb-4">Impossible de charger les messages</p>
        <Button onClick={() => refetch()}>Réessayer</Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6 h-[500px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!allMessages?.length) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">
                Aucun message
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Vos messages s'afficheront ici.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <Card className="border shadow-sm h-[700px] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 h-full">
          {/* Conversations list */}
          <div className="border-r h-full flex flex-col">
            <CardHeader className="py-4 space-y-1 border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-primary" />
                Conversations
              </CardTitle>
            </CardHeader>
            <ScrollArea className="flex-1 overflow-y-auto">
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`p-3 hover:bg-muted/50 transition-colors cursor-pointer flex items-center gap-3 border-b ${
                    selectedConversation?.id === conversation.id ? "bg-muted" : ""
                  }`}
                  onClick={() => setSelectedConversation(conversation)}
                >
                  <Avatar className="h-10 w-10 border flex-shrink-0">
                    <AvatarImage 
                      src={conversation.person?.profile_photo} 
                      alt={conversation.person?.full_name || "User"} 
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {conversation.person?.full_name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <p className="font-medium text-sm truncate">
                        {conversation.person?.full_name || "Utilisateur"}
                      </p>
                      <span className="text-xs text-muted-foreground">
                        {format(conversation.lastMessageTime, "dd/MM", { locale: fr })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-muted-foreground truncate w-32">
                        {conversation.messages[0]?.content || ""}
                      </p>
                      {conversation.unreadCount > 0 && (
                        <Badge variant="default" className="text-[10px] h-5 min-w-5 flex items-center justify-center">
                          {conversation.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>

          {/* Messages content */}
          <div className="col-span-2 flex flex-col h-full">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b flex items-center justify-between bg-card">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 border">
                      <AvatarImage 
                        src={selectedConversation.person?.profile_photo} 
                        alt={selectedConversation.person?.full_name || "User"} 
                      />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {selectedConversation.person?.full_name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-medium">{selectedConversation.person?.full_name || "Utilisateur"}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarClock className="h-3 w-3" />
                        <span>Dernière activité: {format(selectedConversation.lastMessageTime, "dd MMMM à HH:mm", { locale: fr })}</span>
                      </div>
                    </div>
                  </div>
                  <div className="md:flex items-center gap-2 hidden">
                    <Button variant="outline" size="sm" className="gap-1">
                      <User className="h-4 w-4" />
                      <span>Profil</span>
                    </Button>
                  </div>
                </div>

                <ScrollArea className="flex-1 p-4 overflow-y-auto max-h-[500px]" ref={scrollAreaRef}>
                  <div className="space-y-4">
                    {currentMessages.map((message) => {
                      const isFromMe = message.sender_id === currentUserId;
                      const messageDate = new Date(message.created_at);
                      const formattedTime = format(messageDate, "HH:mm", { locale: fr });
                      const formattedDate = format(messageDate, "d MMMM yyyy", { locale: fr });
                      
                      if (!isFromMe && !message.read) {
                        setTimeout(() => markAsRead.mutate(message.id), 1000);
                      }
                      
                      return (
                        <div 
                          key={message.id} 
                          className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                        >
                          <div 
                            className={`max-w-[85%] ${
                              isFromMe 
                                ? 'bg-primary text-primary-foreground rounded-2xl rounded-tr-none' 
                                : 'bg-muted/60 rounded-2xl rounded-tl-none'
                            } p-3 shadow-sm`}
                          >
                            <div className="flex justify-between items-start gap-3 mb-1">
                              <div className="flex items-center gap-2">
                                {!isFromMe && (
                                  <Avatar className="h-6 w-6 flex-shrink-0">
                                    <AvatarImage src={message.sender?.profile_photo} />
                                    <AvatarFallback className="text-xs">{message.sender?.full_name?.charAt(0) || "U"}</AvatarFallback>
                                  </Avatar>
                                )}
                                <span className={`text-sm font-medium ${isFromMe ? 'text-primary-foreground' : ''}`}>
                                  {isFromMe ? "Moi" : message.sender?.full_name || "Utilisateur"}
                                </span>
                              </div>
                              <div className="flex flex-col items-end">
                                <span className={`text-xs ${isFromMe ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                  {formattedTime}
                                </span>
                                <span className={`text-[10px] ${isFromMe ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                  {formattedDate}
                                </span>
                              </div>
                            </div>
                            <p className="text-sm break-words whitespace-pre-wrap">{message.content}</p>
                            <div className="flex justify-end gap-1 mt-2">
                              {isFromMe && message.read && (
                                <span className={`flex items-center text-xs ${isFromMe ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                  <CheckCheck className="h-3 w-3 mr-1" />
                                  Lu
                                </span>
                              )}
                              {!isFromMe && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-6 w-6 ${isFromMe ? 'text-primary-foreground/70 hover:bg-primary-foreground/10' : ''}`}
                                  onClick={() => setReplyTo({ id: message.id, sender: message.sender?.full_name || "Utilisateur" })}
                                >
                                  <Reply className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-6 w-6 ${isFromMe ? 'text-primary-foreground/70 hover:bg-primary-foreground/10' : ''}`}
                                onClick={() => handleDeleteRequest(message.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>

                {replyTo && (
                  <div className="px-4 pt-2 pb-0">
                    <div className="bg-muted/30 rounded p-2 text-xs flex justify-between items-center">
                      <div>
                        <span className="font-medium text-primary">En réponse à {replyTo.sender}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 rounded-full"
                        onClick={() => setReplyTo(null)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="p-4 border-t mt-auto bg-card">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrire un message..."
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey && newMessage.trim()) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <Button 
                      onClick={handleSendMessage} 
                      disabled={!newMessage.trim()}
                      className="flex-shrink-0"
                    >
                      Envoyer
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-6">
                  <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium">Sélectionnez une conversation</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Choisissez une conversation dans la liste pour afficher les messages.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le message</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce message? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Messages;
